import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';

function KnightMark() {
  return (
    <svg width="22" height="22" viewBox="0 0 45 45" aria-hidden="true" className="brand-mark">
      <path
        d="M22 10c10.5 1 16.5 8 16 29H15c0-9 10-6.5 8-21M24 18c.38 2.91-5.55 7.37-8 9-3 2-2.82 4.34-5 4-1.042-.94 1.41-3.04 0-3-1 0 .19 1.23-1 2-1 0-4.003 1-4-4 0-2 6-12 6-12s1.89-1.9 2-3.5c-.73-.994-.5-2-.5-3 1-1 3 2.5 3 2.5h2s.78-1.992 2.5-3c1 0 1 3 1 3"
        fill="currentColor"
        stroke="currentColor"
        strokeWidth={1.2}
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Global header. Browsing and reading theory never require an account, so the
 * header never gates navigation — it only swaps the sign-in link for a user
 * menu once you are signed in.
 */
export default function SiteHeader() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const userRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onOutside(e: MouseEvent) {
      if (userRef.current && !userRef.current.contains(e.target as Node)) setMenuOpen(false);
      if (navRef.current && !navRef.current.contains(e.target as Node)) setNavOpen(false);
    }
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, []);

  // Close the mobile menu whenever the route changes.
  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  const navClass = ({ isActive }: { isActive: boolean }) =>
    `app-nav-link${isActive ? ' app-nav-link--active' : ''}`;

  return (
    <nav className="app-nav">
      <div className="app-nav-left" ref={navRef}>
        <Link to="/" className="app-nav-brand">
          <KnightMark />
          Chess Teacher
        </Link>
        <button
          type="button"
          className="nav-hamburger"
          aria-label="Menu"
          aria-expanded={navOpen}
          onClick={() => setNavOpen((o) => !o)}
        >
          <span />
          <span />
          <span />
        </button>
        <div className={`app-nav-links${navOpen ? ' open' : ''}`}>
          <NavLink to="/" end className={navClass}>
            Today
          </NavLink>
          <NavLink to="/openings" className={navClass}>
            Openings
          </NavLink>
          <NavLink to="/study" className={navClass}>
            Study
          </NavLink>
          <NavLink to="/pathway" className={navClass}>
            Pathway
          </NavLink>
        </div>
      </div>

      <div ref={userRef} className="app-nav-user">
        {user ? (
          <>
            <button type="button" className="user-button" onClick={() => setMenuOpen((v) => !v)}>
              <span className="user-avatar" aria-hidden="true">
                {user.displayName.charAt(0).toUpperCase()}
              </span>
              <span className="user-name">{user.displayName}</span>
              <span className="user-caret" aria-hidden="true">
                {menuOpen ? '▴' : '▾'}
              </span>
            </button>
            {menuOpen && (
              <div className="dropdown">
                <Link to="/profile" onClick={() => setMenuOpen(false)} className="dropdown-item">
                  Profile &amp; settings
                </Link>
                <button
                  type="button"
                  className="dropdown-item dropdown-item--button"
                  onClick={() => {
                    logout();
                    setMenuOpen(false);
                  }}
                >
                  Sign out
                </button>
              </div>
            )}
          </>
        ) : (
          <Link
            to="/login"
            state={{ from: location.pathname + location.search }}
            className="btn btn--ghost"
          >
            Sign in
          </Link>
        )}
      </div>
    </nav>
  );
}
